import React, { useState, useEffect } from 'react';
import { migoService } from '../../services/migoService';
import { supplierService } from '../../services/supplierService';

const SupplierForm = ({
  isPageMode = false,
  supplier = null,
  onSuccess,
  onError,
  showEntityTypes = false,
  forceClientOnly = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    document_type: 'ruc', // 'ruc', 'dni', 'other'
    document_number: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    is_active: true,
    is_provider: true,
    is_client: false,
    is_internal: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [apiSuccessMessage, setApiSuccessMessage] = useState('');

  useEffect(() => {
    if (supplier) {
      // Modo edición
      setFormData({
        name: supplier.name || '',
        document_type: supplier.document_type || 'ruc',
        document_number: supplier.document_number || '',
        contact_name: supplier.contact_name || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: supplier.address || '',
        is_active: supplier.is_active !== undefined ? supplier.is_active : true,
        is_provider: forceClientOnly ? false : showEntityTypes ? (supplier.is_provider !== undefined ? supplier.is_provider : true) : true,
        is_client: forceClientOnly ? true : showEntityTypes ? supplier.is_client === true : false,
        is_internal: showEntityTypes ? supplier.is_internal === true : false,
      });
    } else {
      // Modo creación
      setFormData({
        name: '',
        document_type: 'ruc',
        document_number: '',
        contact_name: '',
        email: '',
        phone: '',
        address: '',
        is_active: true,
        is_provider: forceClientOnly ? false : true,
        is_client: forceClientOnly,
        is_internal: false,
      });
    }
    setErrors({});
  }, [supplier, forceClientOnly]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
      
      // Si cambia el tipo de documento, limpiar el número y generar código si es "other"
      if (name === 'document_type') {
        newData.document_number = '';
        if (value === 'other') {
          // Generar código único para "Otro"
          const timestamp = Date.now().toString().slice(-6);
          const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
          newData.document_number = `OTRO-${timestamp}-${random}`;
        }
      }
      
      return newData;
    });
    
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleDocumentBlur = async () => {
    const docNumber = formData.document_number?.trim();
    const docType = formData.document_type;
    
    // Solo consultar si:
    // - No es "other"
    // - Tiene la longitud correcta (11 para RUC, 8 para DNI)
    // - No hay nombre ya ingresado
    if (docType !== 'other' && docNumber && 
        ((docType === 'ruc' && docNumber.length === 11) || 
         (docType === 'dni' && docNumber.length === 8))) {
      setLoadingDocument(true);
      
      try {
        let response;
        if (docType === 'ruc') {
          response = await migoService.consultRUC(docNumber);
        } else if (docType === 'dni') {
          response = await migoService.consultDNI(docNumber);
        }
        
        console.log('📦 [SupplierForm] Respuesta completa:', response);
        
        if (response && response.success && response.data) {
          const apiData = response.data;
          console.log('📋 [SupplierForm] Datos extraídos de la API:', apiData);
          
          // Extraer nombre y dirección de la respuesta
          const newName = apiData.name ? String(apiData.name).trim() : '';
          const newAddress = apiData.address ? String(apiData.address).trim() : '';
          
          console.log('🔄 [SupplierForm] Nuevo nombre a asignar:', newName);
          console.log('🔄 [SupplierForm] Nueva dirección a asignar:', newAddress);
          
          // Actualizar el estado del formulario directamente
          setFormData(prev => {
            const updated = {
              ...prev,
            };
            
            // Solo actualizar si hay datos nuevos
            if (newName) {
              updated.name = newName;
            }
            if (newAddress) {
              updated.address = newAddress;
            }
            
            console.log('🔄 [SupplierForm] Estado anterior:', prev);
            console.log('🔄 [SupplierForm] Estado actualizado:', updated);
            
            return updated;
          });
          
          // Mostrar mensaje de éxito
          setApiSuccessMessage(`Información de ${docType.toUpperCase()} cargada exitosamente`);
          setTimeout(() => {
            setApiSuccessMessage('');
          }, 3000);
          
          console.log(`✅ [SupplierForm] Información de ${docType.toUpperCase()} cargada exitosamente`);
        } else {
          console.warn(`⚠️ [SupplierForm] No se pudo obtener información del ${docType.toUpperCase()}:`, response?.error);
          console.warn(`📊 [SupplierForm] Respuesta completa recibida:`, response);
          // No mostramos error al usuario, solo no autocompletamos
        }
      } catch (error) {
        console.error(`❌ [SupplierForm] Error al consultar ${docType.toUpperCase()}:`, error);
        console.error(`📊 [SupplierForm] Error completo:`, error.response || error);
        // No mostramos error al usuario, solo no autocompletamos
      } finally {
        setLoadingDocument(false);
      }
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }

    // Validar según el tipo de documento
    if (formData.document_number) {
      if (formData.document_type === 'ruc' && formData.document_number.length !== 11) {
        newErrors.document_number = 'El RUC debe tener 11 dígitos';
      } else if (formData.document_type === 'dni' && formData.document_number.length !== 8) {
        newErrors.document_number = 'El DNI debe tener 8 dígitos';
      }
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    if (showEntityTypes && !formData.is_provider && !formData.is_client && !formData.is_internal) {
      newErrors.entity_types = 'Seleccione al menos un tipo de entidad';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    
    try {
      const supplierData = {
        name: formData.name,
        document_type: formData.document_type,
        document_number: formData.document_number || null,
        contact_name: formData.contact_name || null,
        email: formData.email || null,
        phone: formData.phone || null,
        address: formData.address || null,
        is_active: formData.is_active,
        is_provider: forceClientOnly ? false : showEntityTypes ? formData.is_provider === true : true,
        is_client: forceClientOnly ? true : showEntityTypes ? formData.is_client === true : false,
        is_internal: forceClientOnly ? false : showEntityTypes ? formData.is_internal === true : false,
      };
      
      console.log('📤 [SupplierForm] Enviando datos del proveedor:', supplierData);
      
      let response;
      if (supplier) {
        // Actualizar proveedor existente
        response = await supplierService.updateSupplier(supplier.id, supplierData);
      } else {
        // Crear nuevo proveedor
        response = await supplierService.createSupplier(supplierData);
      }
      
      if (response.success) {
        console.log('✅ [SupplierForm] Proveedor guardado exitosamente');
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error(response.message || 'Error al guardar el proveedor');
      }
    } catch (error) {
      console.error('❌ [SupplierForm] Error al guardar proveedor:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error al guardar el proveedor';
      const errorErrors = error.response?.data?.errors || {};
      
      setErrors({
        submit: errorMessage,
        ...errorErrors,
      });
      
      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {errors.submit && (
          <div className="bg-red-50 text-red-700 p-3 rounded text-sm border border-red-200">
            {errors.submit}
          </div>
        )}
        
        {apiSuccessMessage && (
          <div className="bg-green-50 text-green-700 p-3 rounded text-sm border border-green-200">
            {apiSuccessMessage}
          </div>
        )}

        {/* Información Básica */}
        <div className="border-b border-gray-200 pb-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Información Básica</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Tipo de Documento */}
            <div>
              <label htmlFor="document_type" className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Documento
              </label>
              <select
                id="document_type"
                name="document_type"
                value={formData.document_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="ruc">RUC</option>
                <option value="dni">DNI</option>
                <option value="other">Otro</option>
              </select>
            </div>

            {/* Número de Documento */}
            <div>
              <label htmlFor="document_number" className="block text-sm font-medium text-gray-700 mb-1">
                {formData.document_type === 'ruc' ? 'RUC' : formData.document_type === 'dni' ? 'DNI' : 'Código'}
                {loadingDocument && (
                  <span className="ml-2 text-xs text-teal-600">Consultando...</span>
                )}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="document_number"
                  name="document_number"
                  value={formData.document_number}
                  onChange={handleChange}
                  onBlur={handleDocumentBlur}
                  maxLength={formData.document_type === 'ruc' ? 11 : formData.document_type === 'dni' ? 8 : undefined}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    errors.document_number ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500'
                  }`}
                  placeholder={
                    formData.document_type === 'ruc' 
                      ? '20123456789' 
                      : formData.document_type === 'dni' 
                      ? '43451826' 
                      : 'Código generado automáticamente'
                  }
                  disabled={loadingDocument || formData.document_type === 'other'}
                  readOnly={formData.document_type === 'other'}
                />
                {loadingDocument && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                  </div>
                )}
              </div>
              {errors.document_number && (
                <p className="mt-1 text-sm text-red-600">{errors.document_number}</p>
              )}
              {formData.document_type !== 'other' && (
                <p className="mt-1 text-xs text-gray-500">
                  Ingrese el {formData.document_type.toUpperCase()} y se autocompletará la información si está disponible
                </p>
              )}
              {formData.document_type === 'other' && (
                <p className="mt-1 text-xs text-gray-500">
                  Se ha generado un código único automáticamente
                </p>
              )}
            </div>
          </div>

          {/* Nombre */}
          <div className="mt-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Nombre o Razón Social <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.name ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500'
              }`}
              placeholder="Ej: Distribuidora ABC S.A.C."
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>
        </div>

        {/* Información de Contacto */}
        <div className="border-b border-gray-200 pb-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Información de Contacto</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Nombre de Contacto */}
            <div>
              <label htmlFor="contact_name" className="block text-sm font-medium text-gray-700 mb-1">
                Nombre de Contacto
              </label>
              <input
                type="text"
                id="contact_name"
                name="contact_name"
                value={formData.contact_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Ej: Juan Pérez"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  errors.email ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500'
                }`}
                placeholder="contacto@proveedor.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Teléfono */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono
              </label>
              <input
                type="text"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="+51 987 654 321"
              />
            </div>

            {/* Dirección */}
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                Dirección
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Av. Principal 123, Lima"
              />
            </div>
          </div>
        </div>

        {/* Opciones */}
        <div className="border-b border-gray-200 pb-4">
          {showEntityTypes && (
            <>
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Entidades</h2>

              <div className="space-y-2 mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_provider"
                    checked={formData.is_provider}
                    onChange={handleChange}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Proveedor</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_client"
                    checked={formData.is_client}
                    onChange={handleChange}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Cliente</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_internal"
                    checked={formData.is_internal}
                    onChange={handleChange}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Interno</span>
                </label>
                {errors.entity_types && (
                  <p className="mt-1 text-sm text-red-600">{errors.entity_types}</p>
                )}
              </div>
            </>
          )}

          <h2 className="text-lg font-semibold text-gray-800 mb-4">Opciones</h2>
          
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Proveedor activo</span>
            </label>
          </div>
        </div>

        {/* Botones */}
        <div className="flex justify-end space-x-4 pt-4">
          <button
            type="button"
            onClick={() => {
              if (isPageMode && window.history.length > 1) {
                window.history.back();
              }
            }}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? 'Guardando...' : supplier ? 'Actualizar' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SupplierForm;

